import 'styles/base.css';
import Header from '@components/common/Header';
import Footer from '@components/common/Footer';
import { ThemeProvider } from 'next-themes';
import Head from 'next/head';
import { useEffect, useMemo, useState } from 'react';
import * as FullStory from '@fullstory/browser';
import { useRouter } from 'next/router';
import { AppProps } from 'next/app';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { archimedesTheme } from 'styles/theme';
import { GlobalStyle } from 'styles/theme/GlobalStyles';
import '@rainbow-me/rainbowkit/styles.css';
import { RainbowKitProvider } from '@rainbow-me/rainbowkit';
import { configureChains, createClient, useAccount, WagmiConfig } from 'wagmi';
import { mainnet, goerli } from 'wagmi/chains';
import { publicProvider } from 'wagmi/providers/public';
import { metaMaskWallet } from '@rainbow-me/rainbowkit/wallets';
import { connectorsForWallets } from '@rainbow-me/rainbowkit';
import { Main } from '@components/containers/Main/Main';
import { awsChain, fullstoryId, mixpanelId } from 'constants/index';
import mixpanel from 'mixpanel-browser';

export default function MyApp({ Component, pageProps }: AppProps) {
  useEffect(() => {
    if (FullStory.isInitialized() === false) {
      FullStory.init({ orgId: fullstoryId });
    }
  }, []);
  mixpanel.init(mixpanelId, {
    debug: process.env.NEXT_PUBLIC_ENVMODE !== 'PRODUCTION',
  });

  const router = useRouter();
  const path = router.pathname;

  const bgColor = useMemo(() => {
    return archimedesTheme.colors.functional.gray2;
  }, [path]);

  const { chains, provider } = configureChains([mainnet, goerli, awsChain], [publicProvider()]);

  const connectors = connectorsForWallets([
    {
      groupName: 'Recommended',
      wallets: [metaMaskWallet({ chains })],
    },
  ]);

  const wagmiClient = createClient({
    autoConnect: true,
    connectors,
    provider,
  });

  // Loading state
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (typeof window !== 'undefined') setLoading(false);
    const handleRouteChange = (url: string) => {
      mixpanel.track('Page Changed', { url });
    };
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, []);

  return (
    <ThemeProvider attribute="class" enableSystem={false} forcedTheme="light">
      <WagmiConfig client={wagmiClient}>
        <RainbowKitProvider chains={chains} modalSize="compact">
          <StyledThemeProvider theme={archimedesTheme}>
            <Head>
              <meta name="viewport" content="width=device-width, initial-scale=1" />
            </Head>
            <GlobalStyle />
            <div
              style={{ backgroundColor: bgColor, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
              className={loading ? 'loading' : 'loaded'}
            >
              <Header></Header>
              <Main>
                <Component {...pageProps} />
              </Main>
              <Footer></Footer>
            </div>
          </StyledThemeProvider>
        </RainbowKitProvider>
      </WagmiConfig>
    </ThemeProvider>
  );
}
